import { makeObservable, observable, action, reaction } from 'mobx';
import { RecipeState, RecipeTemplateDto } from '../types';
import { GroupStore } from './GroupStore';
import { FormulaScope } from './FormulaScope';
import { log } from '@/lib/utils/logger';

/**
 * Main MobX store for recipe data.
 *
 * Replaces Knockout's RecipeVM with MobX observables:
 * - Observable groups/rows/values (like ko.observable)
 * - Automatic dependency tracking for formulas (like ko.computed)
 * - Reaction to sync changes to Zustand (like ko.subscribe)
 *
 * This gives us Knockout's reactive formula behavior in React.
 */
export class RecipeStore {
  groups: GroupStore[] = [];
  runtimeChangedManually: boolean = false;
  templateName: string = '';
  templateHash: string | undefined = undefined;

  private formulaScope: FormulaScope;
  private template: RecipeTemplateDto;
  private syncReactionDisposer?: () => void;
  private runtimeReactionDisposer?: () => void;

  constructor(
    initialData: RecipeState,
    template: RecipeTemplateDto,
    onChange?: () => void,
    onRuntimeCalculated?: (runtime: number) => void
  ) {
    this.template = template;
    this.runtimeChangedManually = initialData.runtimeChangedManually || false;
    this.templateName = initialData.templateName;
    this.templateHash = initialData.templateHash;

    // Create formula scope
    this.formulaScope = new FormulaScope(this, template.formulaConstants, template.calcRuntimeFormula);

    // Create group stores
    this.groups = initialData.groups.map((groupData) => new GroupStore(groupData, this, this.formulaScope));

    makeObservable(this, {
      groups: observable,
      runtimeChangedManually: observable,
      templateName: observable,
      templateHash: observable,
      addGroup: action,
      removeGroup: action,
      setRuntimeChangedManually: action,
    });

    // Set up reaction to notify parent (Zustand) of changes
    // CRITICAL: Only track raw input values, NOT computed formulas
    // Computed formulas are derived data - they don't trigger saves, only their inputs do
    if (onChange) {
      this.syncReactionDisposer = reaction(
        () => {
          // Track only raw input values that users can change
          // This prevents cycles from computed values triggering the reaction
          return this.groups.map((group) =>
            group.rows.map((row) => {
              // Only observe row.values (raw inputs), not getValue() (computed)
              // This is like Knockout's ko.observable changes, not ko.computed changes
              return Array.from(row.values.entries());
            })
          );
        },
        () => {
          log.debug('[RecipeStore] User input changed, notifying parent (Zustand sync)');
          onChange();
        },
        {
          // Do NOT use delay here: MobX schedules the effect with setTimeout(delay). If RecipeStore is
          // disposed before that fires (store recreated when recipeData/template deps change, Strict Mode,
          // or leaving edit mode), the callback is dropped and onChange never runs — recipe edits never
          // marked dirty. Sync scheduling runs in the same tick as the values mutation.
          equals: () => false, // Always treat expression output as changed when the effect runs (cheap vs deep compare)
        }
      );
    }

    // Set up reaction for runtime calculation (replaces useEffect in Recipe.tsx)
    // Only tracks the specific field referenced by calcRuntimeFormula, not the entire store
    // Defer setup to next tick to avoid firing during initialization when all rows are being created
    if (onRuntimeCalculated && template.calcRuntime && template.calcRuntimeFormula) {
      const calcRuntimeFormula = template.calcRuntimeFormula; // Type narrowing
      // Defer reaction setup to avoid firing during initialization
      // This prevents 180 calculations when initializing a recipe with 180 rows
      setTimeout(() => {
        this.runtimeReactionDisposer = this.setupRuntimeReaction(calcRuntimeFormula, onRuntimeCalculated);
        // Calculate initial runtime value after setup
        this.calculateInitialRuntime(onRuntimeCalculated);
      }, 0);
    }
  }

  /**
   * Read the current runtime value from the recipe field referenced by {@link RecipeTemplateDto.calcRuntimeFormula}.
   */
  getCalculatedRuntimeFromRecipe(): number | undefined {
    const calcRuntimeFormula = this.template.calcRuntimeFormula;
    if (!calcRuntimeFormula) {
      return undefined;
    }

    const fields = calcRuntimeFormula.split(',');
    if (fields.length < 2) {
      return undefined;
    }

    const groupSymbol = fields[0].trim();
    const columnSymbol = fields[1].trim();
    const rowSymbol = fields.length > 2 ? fields[2].trim() : undefined;

    const group = this.groups.find((g) => g.symbol === groupSymbol);
    if (!group) {
      return undefined;
    }

    const row = rowSymbol ? group.rows.find((r) => r.symbol === rowSymbol) : group.rows[0];
    if (!row) {
      return undefined;
    }

    const value = row.getValue(columnSymbol);
    const runtime = typeof value === 'number' ? value : parseFloat(value);
    return !isNaN(runtime) ? runtime : undefined;
  }

  /**
   * Calculate initial runtime value after store is fully initialized.
   * This is called once after the reaction is set up.
   */
  private calculateInitialRuntime(onRuntimeCalculated: (runtime: number) => void): void {
    try {
      const runtime = this.getCalculatedRuntimeFromRecipe();

      if (runtime != null && !this.runtimeChangedManually) {
        log.debug('[RecipeStore] Initial runtime calculated:', runtime);
        onRuntimeCalculated(runtime);
      }
    } catch (error) {
      log.error('[RecipeStore] Error calculating initial runtime:', error);
    }
  }

  /**
   * Set up MobX reaction to calculate runtime when the referenced field changes.
   * This replaces the useEffect in Recipe.tsx and only tracks the specific value,
   * preventing unnecessary recalculations when other parts of the store change.
   *
   * Tracks both:
   * 1. The specific field value (for when the value changes)
   * 2. The row count in the group (for when rows are added/removed)
   *
   * Uses debouncing to batch calculations during initialization.
   */
  private setupRuntimeReaction(calcRuntimeFormula: string, onRuntimeCalculated: (runtime: number) => void): () => void {
    try {
      // Parse calcRuntimeFormula: "group,column" or "group,column,row"
      const fields = calcRuntimeFormula.split(',');
      if (fields.length < 2) {
        log.warn('[RecipeStore] Invalid calcRuntimeFormula format:', calcRuntimeFormula);
        return () => {}; // Return no-op disposer
      }

      const groupSymbol = fields[0].trim();
      const columnSymbol = fields[1].trim();
      const rowSymbol = fields.length > 2 ? fields[2].trim() : undefined;

      // Track both the value and row count to detect row additions/removals
      // This ensures we recalculate when rows are added/removed, but debouncing
      // prevents 180 calculations during initialization
      return reaction(
        () => {
          const group = this.groups.find((g) => g.symbol === groupSymbol);
          if (!group) return { value: undefined, rowCount: 0 };

          const row = rowSymbol ? group.rows.find((r) => r.symbol === rowSymbol) : group.rows[0];
          if (!row) return { value: undefined, rowCount: group.rows.length };

          // Match RecipeCell: track cacheVersion so cross-group formula invalidation
          // (e.g. summary.t_h = sum(steps.t_h)) re-triggers runtime when step inputs change.
          this.groups.forEach((g) => {
            g.rows.forEach((r) => {
              void r.cacheVersion;
            });
          });

          return {
            value: row.getValue(columnSymbol),
            rowCount: group.rows.length,
          };
        },
        ({ value }) => {
          if (this.runtimeChangedManually) return; // Don't override manual changes

          const runtime = typeof value === 'number' ? value : parseFloat(value);
          if (!isNaN(runtime)) {
            log.debug('[RecipeStore] Runtime calculated from field reference:', runtime);
            onRuntimeCalculated(runtime);
          }
        },
        {
          delay: 200, // Longer debounce to batch calculations during initialization
          // Fire immediately on first run (after initialization)
          fireImmediately: false,
        }
      );
    } catch (error) {
      log.error('[RecipeStore] Error setting up runtime reaction:', error);
      return () => {}; // Return no-op disposer
    }
  }

  /**
   * Dispose of runtime reaction (cleanup)
   */
  dispose(): void {
    if (this.syncReactionDisposer) {
      this.syncReactionDisposer();
      this.syncReactionDisposer = undefined;
    }
    if (this.runtimeReactionDisposer) {
      this.runtimeReactionDisposer();
      this.runtimeReactionDisposer = undefined;
    }
  }

  /**
   * Find a group by symbol
   */
  getGroup(symbol: string): GroupStore | undefined {
    return this.groups.find((g) => g.symbol === symbol);
  }

  /**
   * Add a new group (copy of existing)
   */
  addGroup(sourceGroupIndex: number): void {
    const sourceGroup = this.groups[sourceGroupIndex];
    if (!sourceGroup) {
      log.warn(`[RecipeStore] Source group ${sourceGroupIndex} not found`);
      return;
    }

    // Create new group data
    // Use sequential IDs for new group and rows
    const maxGroupId = this.groups.reduce((max, group) => {
      const groupId = group.id ?? 0;
      return Math.max(max, groupId > 0 ? groupId : 0);
    }, 0);
    const newGroupData = {
      ...sourceGroup.toState(),
      id: maxGroupId + 1,
      rows: sourceGroup.rows.map((row, index) => ({
        ...row.toState(),
        id: index + 1,
        definedInTemplate: false,
      })),
    };

    const newGroup = new GroupStore(newGroupData, this, this.formulaScope);
    this.groups.push(newGroup);

    log.debug(`[RecipeStore] Added new group`);
  }

  /**
   * Remove a group
   */
  removeGroup(groupIndex: number): void {
    if (groupIndex < 0 || groupIndex >= this.groups.length) {
      log.warn(`[RecipeStore] Invalid group index ${groupIndex}`);
      return;
    }

    this.groups.splice(groupIndex, 1);
    log.debug(`[RecipeStore] Removed group ${groupIndex}`);
  }

  /** Mark whether the user has manually overridden recipe-calculated runtime. */
  setRuntimeChangedManually(changed: boolean): void {
    this.runtimeChangedManually = changed;
  }

  /**
   * Export to plain object for Zustand sync
   */
  toState(): RecipeState {
    return {
      groups: this.groups.map((group) => group.toState()),
      runtimeChangedManually: this.runtimeChangedManually,
      templateName: this.templateName,
      templateHash: this.templateHash,
    };
  }

  /**
   * Get the formula scope (for external use if needed)
   */
  getFormulaScope(): FormulaScope {
    return this.formulaScope;
  }

  /**
   * Get the template (for runtime calculations)
   */
  getTemplate(): RecipeTemplateDto {
    return this.template;
  }
}
